"use client";

import * as React from "react";
import Link from "next/link";
import { createPortal } from "react-dom";
import { GraduationCap, Menu, X, ChevronDown } from "lucide-react";
import { buttonVariants } from "@/components/ui";
import { cn } from "@/lib/cn";
import type { SafeUser } from "@/types/auth";
import { useAuthModal } from "@/features/auth/auth-modal";
import { gradeNav, gradeNavLabel, marketingNav } from "./marketing-nav";

const drawerLinkClass =
  "flex items-center rounded-md px-3 py-3 text-body font-medium text-foreground-secondary hover:bg-subtle hover:text-foreground " +
  "focus-visible:outline-none focus-visible:ring focus-visible:ring-ring";

/**
 * Menu điều hướng cho màn hình nhỏ (<lg): nút 3 gạch mở drawer bên phải.
 * Dùng portal vì header có backdrop-blur — phần tử fixed bên trong sẽ bị
 * neo theo header thay vì viewport.
 */
export function MarketingMobileNav({ user }: { user: SafeUser | null }) {
  const { openAuth } = useAuthModal();
  const [open, setOpen] = React.useState(false);
  const [gradesOpen, setGradesOpen] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => setMounted(true), []);

  React.useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panelRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Đổi sang breakpoint desktop khi drawer đang mở: đóng lại để nhả khóa scroll.
  React.useEffect(() => {
    if (!open) return;
    const mq = window.matchMedia("(min-width: 1024px)");
    function onChange() {
      if (mq.matches) setOpen(false);
    }
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [open]);

  function close() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Mở menu"
        aria-expanded={open}
        aria-haspopup="dialog"
        className="grid size-9 shrink-0 place-items-center rounded-md text-foreground-secondary transition-colors hover:bg-subtle hover:text-foreground focus-visible:outline-none focus-visible:ring focus-visible:ring-ring lg:hidden"
      >
        <Menu className="size-5" aria-hidden="true" />
      </button>

      {mounted &&
        open &&
        createPortal(
          <div className="fixed inset-0 z-50 lg:hidden">
            <div
              className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
              onClick={close}
              aria-hidden="true"
            />

            <div
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              aria-label="Điều hướng"
              tabIndex={-1}
              className="absolute inset-y-0 right-0 flex w-[86%] max-w-sm flex-col bg-surface shadow-overlay outline-none"
            >
              <div className="flex h-16 shrink-0 items-center justify-between gap-3 border-b border-border px-4">
                <Link href="/" onClick={close} className="flex min-w-0 items-center gap-2">
                  <span className="grid size-9 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground">
                    <GraduationCap className="size-5" aria-hidden="true" />
                  </span>
                  <span className="truncate text-body font-bold text-foreground">
                    Cô Giáo Tiểu Học
                  </span>
                </Link>
                <button
                  type="button"
                  onClick={close}
                  aria-label="Đóng menu"
                  className="grid size-9 shrink-0 place-items-center rounded-md text-foreground-secondary hover:bg-subtle hover:text-foreground focus-visible:outline-none focus-visible:ring focus-visible:ring-ring"
                >
                  <X className="size-5" aria-hidden="true" />
                </button>
              </div>

              <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto p-3" aria-label="Điều hướng">
                <button
                  type="button"
                  onClick={() => setGradesOpen((v) => !v)}
                  aria-expanded={gradesOpen}
                  className={cn(drawerLinkClass, "w-full justify-between gap-2 text-left")}
                >
                  {gradeNavLabel}
                  <ChevronDown
                    className={cn(
                      "size-4 shrink-0 transition-transform duration-fast",
                      gradesOpen && "rotate-180"
                    )}
                    aria-hidden="true"
                  />
                </button>
                {gradesOpen && (
                  <div className="space-y-1 pl-3">
                    {gradeNav.map((item) => (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={close}
                        className={cn(drawerLinkClass, "py-2.5 text-body-sm")}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}

                {marketingNav.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={close}
                    className={drawerLinkClass}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>

              <div className="shrink-0 space-y-2 border-t border-border p-4">
                {user ? (
                  <Link
                    href="/bang-dieu-khien"
                    onClick={close}
                    className={buttonVariants({ block: true })}
                  >
                    Vào bảng điều khiển
                  </Link>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        close();
                        openAuth("register");
                      }}
                      className={buttonVariants({ block: true })}
                    >
                      Đăng ký
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        close();
                        openAuth("login");
                      }}
                      className={buttonVariants({ variant: "secondary", block: true })}
                    >
                      Đăng nhập
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
